import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Menu, MenuItem } from "@mui/material";
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from "material-ui-popup-state/hooks";
import { MuiIconButton } from "../../../../../../components";
import PermissionWrapper from "../../../../../../components/permissionWrapper/PermissionWrapper";
import {
  STATUS_BADGE_TYPE,
  StatusBadgeType,
} from "../../../../../../components/statusBadge/StatusBadge";
import { MODULE_PERMISSIONS } from "../../../../../../helpers/constants";
import { COMPONENT_IDS } from "../../../../../../helpers/constants/componentIds";
import {
  getValueOrBlank,
  isDocumentInherited,
  isStatusActive,
} from "../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { ClientDocument } from "../../../../modelsAndSchemas/Models";
import { documentSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { OptionMenuWrapper } from "./OptionMenu.style";

export interface OptionMenuProps {
  clientData: ClientDocument;
  onClickOptionEdit: (row: ClientDocument) => void;
  onClickOptionDelete: (row: ClientDocument) => void;
  onClickArchive: (row: ClientDocument) => void;
  onClickOptionMarkAsActive: (row: ClientDocument) => void;
  onClickOptionNoLongerValid: (row: ClientDocument) => void;
  onClickOptionUnMap?: (row: ClientDocument) => void;
}

export const OptionMenu: React.FC<OptionMenuProps> = ({
  clientData,
  onClickOptionEdit,
  onClickOptionDelete,
  onClickArchive,
  onClickOptionNoLongerValid,
  onClickOptionMarkAsActive,
  onClickOptionUnMap,
}) => {
  const { commonLabels, fetchLabel } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const popupState = usePopupState({
    popupId: `option-menu-${clientData.documentId}`,
    variant: "popover",
  });

  const markAsNoLongerValidLabel = fetchLabel(
    documentSiteCoreKeys.documentsTitle,
    documentSiteCoreKeys.markAsNoLongerValidAction,
  );

  return (
    <OptionMenuWrapper>
      <MuiIconButton
        className="option-menu-icon"
        {...bindTrigger(popupState)}
        id={`${COMPONENT_IDS.COMMON_OPTION_MENU}-${clientData.documentId}`}
        data-testid={`${COMPONENT_IDS.COMMON_OPTION_MENU}-${clientData.documentId}`}
        aria-label={commonARIALabel.moreOptionsForThisTableRow}
      >
        <MoreVertIcon />
      </MuiIconButton>

      <Menu {...bindMenu(popupState)}>
        {clientData.status !== STATUS_BADGE_TYPE.noLongerValid && (
          <>
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.EDIT_CLIENT_DOCUMENTS}
            >
              <MenuItem
                data-testid={`${COMPONENT_IDS.COMMON_OPTION_MENU_EDIT}-${clientData.documentId}`}
                onClick={() => {
                  popupState.close();
                  onClickOptionEdit(clientData);
                }}
              >
                {commonLabels.edit}
              </MenuItem>
            </PermissionWrapper>
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.DELETE_CLIENT_DOCUMENTS}
            >
              <MenuItem
                data-testid={`${COMPONENT_IDS.COMMON_OPTION_MENU_DELETE}-${clientData.documentId}`}
                onClick={() => {
                  popupState.close();
                  onClickOptionDelete(clientData);
                }}
              >
                {commonLabels.delete}
              </MenuItem>
            </PermissionWrapper>
          </>
        )}
        {clientData.status === STATUS_BADGE_TYPE.active &&
          !clientData.isCancelled && (
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.EDIT_CLIENT_DOCUMENTS}
            >
              <MenuItem
                data-testid={`${COMPONENT_IDS.OPTION_MENU_CANCEL_CONTRACT}-${clientData.documentId}`}
                onClick={() => {
                  popupState.close();
                  onClickOptionNoLongerValid(clientData);
                }}
              >
                {markAsNoLongerValidLabel}
              </MenuItem>
            </PermissionWrapper>
          )}
        {clientData.status === STATUS_BADGE_TYPE.noLongerValid && (
          <>
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.EDIT_CLIENT_DOCUMENTS}
            >
              <MenuItem
                data-testid={`${COMPONENT_IDS.OPTION_MENU_MARK_AS_ACTIVE}-${clientData.documentId}`}
                onClick={() => {
                  popupState.close();
                  onClickOptionMarkAsActive(clientData);
                }}
              >
                {commonLabels.markAsActive}
              </MenuItem>
              <MenuItem
                data-testid={`${COMPONENT_IDS.OPTION_MENU_MARK_AS_EXPIRED}-${clientData.documentId}`}
                onClick={() => {
                  popupState.close();
                  onClickArchive(clientData);
                }}
              >
                {commonLabels.archive}
              </MenuItem>
            </PermissionWrapper>
          </>
        )}
        {isDocumentInherited(getValueOrBlank(clientData.documentOrigin)) &&
          isStatusActive(clientData.status as StatusBadgeType) && (
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.EDIT_CLIENT_DOCUMENTS}
            >
              <MenuItem
                data-testid={`${COMPONENT_IDS.COMMON_OPTION_MENU_UNMAP}-${clientData.documentId}`}
                onClick={() => {
                  popupState.close();
                  onClickOptionUnMap?.(clientData);
                }}
              >
                {commonLabels.unmap}
              </MenuItem>
            </PermissionWrapper>
          )}
      </Menu>
    </OptionMenuWrapper>
  );
};
